import { useState, useCallback, useRef } from "react";
import { usePolling } from "./usePolling";
import * as messageApi from "../api/message.api";
import type { MessageWithSender } from "../api/message.api";

/**
 * Poll for unread message count across all conversations.
 * Active tab: 10s, Background: 30s.
 */
export function useUnreadCount(enabled: boolean) {
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchCount = useCallback(async () => {
    const { unreadCount: count } = await messageApi.getUnreadCount();
    setUnreadCount(count);
  }, []);

  usePolling(fetchCount, {
    activeInterval: 10_000,
    backgroundInterval: 30_000,
    enabled,
  });

  return unreadCount;
}

/**
 * Poll for new messages in a specific conversation.
 * Active tab: 3s, Inbox: 10s, Background: 30s.
 */
export function useConversationPoll(
  conversationId: string | null,
  onNewMessages: (msgs: MessageWithSender[]) => void
) {
  const sinceRef = useRef(new Date().toISOString());

  const poll = useCallback(async () => {
    if (!conversationId) return;
    const { messages, serverTime } = await messageApi.pollMessages(sinceRef.current);
    sinceRef.current = serverTime;

    // Filter messages for this conversation only
    const relevant = messages.filter((m) => m.conversationId === conversationId);
    if (relevant.length > 0) {
      onNewMessages(relevant);
    }
  }, [conversationId, onNewMessages]);

  usePolling(poll, {
    activeInterval: 3_000,
    backgroundInterval: 30_000,
    enabled: !!conversationId,
  });
}
